import { generateUniqueId } from '../../utils/form-utils';
import {
  ExamContentEcosResult,
  ExamContentEcosV2Result,
  UpdateExamContentEcosPatientPictogramResult,
  UpdateExamContentEcosPictureResult,
} from '../api-results/exam-content-ecos.api-result';
import { EcosMode, EcosPatientType, EcosVersion } from '../enums/ecos.enum';
import {
  ExamContentEcosPatientFormValue,
  ExamContentEcosPatientPayload,
} from '../payloads/exam-content-ecos-patient.payload';
import {
  ExamContentEcosFormValue,
  UpdateExamContentEcosPayload,
} from '../payloads/exam-content-ecos.payload';
import { Course } from './course.entity';
import { ExamContent } from './exam-content.entity';

export class ExamContentEcos {
  subject: string;
  instructions: string;
  picture: string;
  preparationTime: number;
  patientPictogram: string;
  patientName: string;
  patientAge: number;
  socioEconomicStatus: string;
  consultationReasons: string;
  totalActionsAuthorized: number;
  courses: Course[];
  version: EcosVersion;

  examContent: ExamContent;

  constructor(result: ExamContentEcosResult) {
    this.subject = result.subject;
    this.instructions = result.instructions;
    this.picture = result.picture;
    this.preparationTime = result.preparationTime;
    this.patientPictogram = result.patientPictogram;
    this.patientName = result.patientName;
    this.patientAge = result.patientAge;
    this.socioEconomicStatus = result.socioEconomicStatus;
    this.consultationReasons = result.consultationReasons;
    this.totalActionsAuthorized = result.totalActionsAuthorized;
    if (result.courses) this.courses = result.courses.map((elt) => new Course(elt));
    this.version = result.version;
    if (result.examContent) this.examContent = new ExamContent(result.examContent);
  }

  // nnkitodo [v2later] exam content ecos form value ce serait mieux
  toFormValue(): ExamContentEcosFormValue {
    return {
      subject: this.subject,
      instructions: this.instructions,
      picture: {
        file: null,
        path: this.picture,
      },
      preparationTime: this.preparationTime / 60,
      patientPictogram: {
        file: null,
        path: this.patientPictogram,
      },
      patientName: this.patientName,
      patientAge: this.patientAge,
      socioEconomicStatus: this.socioEconomicStatus,
      consultationReasons: this.consultationReasons,
      totalActionsAuthorized: this.totalActionsAuthorized,
    };
  }

  toResult(): ExamContentEcosResult {
    return {
      subject: this.subject,
      instructions: this.instructions,
      picture: this.picture,
      preparationTime: this.preparationTime,
      patientPictogram: this.patientPictogram,
      patientName: this.patientName,
      patientAge: this.patientAge,
      socioEconomicStatus: this.socioEconomicStatus,
      consultationReasons: this.consultationReasons,
      totalActionsAuthorized: this.totalActionsAuthorized,
      courses: this.courses?.map((elt) => elt.toResult()),
      version: this.version,
      examContent: undefined,
    };
  }

  static updatedEntityToResult(
    payload: UpdateExamContentEcosPayload,
    updatePictureResult: UpdateExamContentEcosPictureResult,
    updatePatientPictogramResult: UpdateExamContentEcosPatientPictogramResult
  ): Partial<ExamContentEcosResult> {
    return {
      subject: payload.subject,
      instructions: payload.instructions,
      picture: updatePictureResult.picture,
      preparationTime: payload.preparationTime,
      patientPictogram: updatePatientPictogramResult.patientPictogram,
      patientName: payload.patientName,
      patientAge: payload.patientAge,
      socioEconomicStatus: payload.socioEconomicStatus,
      consultationReasons: payload.consultationReasons,
      totalActionsAuthorized: payload.totalActionsAuthorized,
    };
  }
}

export class ExamContentEcosV2 extends ExamContentEcos {
  mode: EcosMode;
  morphology: string;
  lifeStyle: string;
  medicalHistory: string;
  surgeryHistory: string;
  familyHistory: string;
  treatment: string;
  allergies: string;
  diseaseHistory: string;
  symptoms: string;
  clinicalExamination: string;
  paraClinicalExamination: string;
  patientAdditionalData: string[];

  constructor(result: ExamContentEcosV2Result) {
    super(result);
    this.mode = result.mode;
    this.morphology = result.morphology;
    this.lifeStyle = result.lifeStyle;
    this.medicalHistory = result.medicalHistory;
    this.surgeryHistory = result.surgeryHistory;
    this.familyHistory = result.familyHistory;
    this.treatment = result.treatment;
    this.allergies = result.allergies;
    this.diseaseHistory = result.diseaseHistory;
    this.symptoms = result.symptoms;
    this.clinicalExamination = result.clinicalExamination;
    this.paraClinicalExamination = result.paraClinicalExamination;
    this.patientAdditionalData = result.patientAdditionalData;
  }

  toPatientFormValue(): ExamContentEcosPatientFormValue {
    return {
      mode: this.mode,
      patientInformations: [
        ...(this.morphology
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.MORPHOLOGY,
                value: this.morphology,
              },
            ]
          : []),
        ...(this.lifeStyle
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.LIFESTYLE,
                value: this.lifeStyle,
              },
            ]
          : []),
        ...(this.medicalHistory
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.MEDICAL_HISTORY,
                value: this.medicalHistory,
              },
            ]
          : []),
        ...(this.surgeryHistory
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.SURGERY_HISTORY,
                value: this.surgeryHistory,
              },
            ]
          : []),
        ...(this.familyHistory
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.FAMILY_HISTORY,
                value: this.familyHistory,
              },
            ]
          : []),
        ...(this.treatment
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.TREATMENT,
                value: this.treatment,
              },
            ]
          : []),
        ...(this.allergies
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.ALLERGIES,
                value: this.allergies,
              },
            ]
          : []),
        ...(this.diseaseHistory
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.DISEASE_HISTORY,
                value: this.diseaseHistory,
              },
            ]
          : []),
        ...(this.symptoms
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.SYMPTOMS,
                value: this.symptoms,
              },
            ]
          : []),
        ...(this.clinicalExamination
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.CLINICAL_EXAMINATION,
                value: this.clinicalExamination,
              },
            ]
          : []),
        ...(this.paraClinicalExamination
          ? [
              {
                id: generateUniqueId(),
                type: EcosPatientType.PARACLINICAL_EXAMINATION,
                value: this.paraClinicalExamination,
              },
            ]
          : []),
        ...(this.patientAdditionalData?.length
          ? this.patientAdditionalData.map((additionalData) => {
              return {
                id: generateUniqueId(),
                type: EcosPatientType.OTHER,
                label: additionalData.split(':::')[0],
                value: additionalData.split(':::')[1],
              };
            })
          : []),
      ],
    };
  }

  static updatedEntityV2ToResult(
    payload: ExamContentEcosPatientPayload
  ): Partial<ExamContentEcosV2Result> {
    return {
      mode: payload.mode,
      morphology: payload.morphology,
      lifeStyle: payload.lifeStyle,
      medicalHistory: payload.medicalHistory,
      surgeryHistory: payload.surgeryHistory,
      familyHistory: payload.familyHistory,
      treatment: payload.treatment,
      allergies: payload.allergies,
      diseaseHistory: payload.diseaseHistory,
      symptoms: payload.symptoms,
      clinicalExamination: payload.clinicalExamination,
      paraClinicalExamination: payload.paraClinicalExamination,
      patientAdditionalData: payload.patientAdditionalData,
    };
  }
}
